import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { Spinner, Toast, css, PageHeader, Badge } from '../components/UI';

const FUENTES = [
  { tipo: 'Pago',          endpoint: '/solicitudes',    color: 'var(--plasma)' },
  { tipo: 'Reembolso',     endpoint: '/reembolsos',     color: 'var(--st-por-pagar)' },
  { tipo: 'Finiquito',     endpoint: '/finiquitos',     color: '#7c3aed' },
  { tipo: 'Comprobación',  endpoint: '/comprobaciones', color: 'var(--ok)' },
];

const fmtMonto = (n) => `$${(parseFloat(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
const fmtFecha = (d) => d ? new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const ENDPOINT_POR_TIPO = Object.fromEntries(FUENTES.map(f => [f.tipo, f.endpoint]));

export default function Papelera() {
  const { user } = useAuth();
  // El contador también puede restaurar (el backend lo limita a sus empresas)
  const esAdmin = ['admin', 'contador'].includes(user?.rol);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [restaurando, setRestaurando] = useState(null);
  const [filtros, setFiltros] = useState({ tipo: '', empresa: '', solicitante: '', desde: '', hasta: '' });
  const [solicInput, setSolicInput] = useState('');
  const [empresas, setEmpresas] = useState([]);
  const toast_ = (msg, tipo = 'ok') => { setToast({ msg, tipo }); setTimeout(() => setToast(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ borradas: '1', limit: '500' });
      if (filtros.empresa) qs.set('empresa', filtros.empresa);
      if (filtros.solicitante) qs.set('solicitante', filtros.solicitante);
      if (filtros.desde) qs.set('desde', filtros.desde);
      if (filtros.hasta) qs.set('hasta', filtros.hasta);
      const fuentes = filtros.tipo ? FUENTES.filter(f => f.tipo === filtros.tipo) : FUENTES;
      const results = await Promise.all(fuentes.map(f =>
        api.get(`${f.endpoint}?${qs.toString()}`)
          .then(d => (d.items || []).map(i => ({ ...i, _tipo: f.tipo, _color: f.color })))
          .catch(() => [])
      ));
      const all = results.flat().sort((a, b) =>
        new Date(b.fechaBorrado || b.createdAt) - new Date(a.fechaBorrado || a.createdAt));
      setItems(all);
    } catch (e) { toast_(e.message, 'error'); }
    finally { setLoading(false); }
  }, [filtros]);

  useEffect(() => { load(); }, [load]);

  // Catálogo de empresas para el select (una sola vez)
  useEffect(() => {
    api.get('/empresas').then(setEmpresas).catch(() => setEmpresas([]));
  }, []);

  // Búsqueda por solicitante con debounce (400 ms tras dejar de escribir)
  useEffect(() => {
    const t = setTimeout(() => {
      setFiltros(f => f.solicitante === solicInput ? f : { ...f, solicitante: solicInput });
    }, 400);
    return () => clearTimeout(t);
  }, [solicInput]);

  const restaurar = async (item) => {
    const endpoint = ENDPOINT_POR_TIPO[item._tipo];
    if (!endpoint) return;
    if (!window.confirm(`¿Restaurar ${item._tipo.toLowerCase()} ${item.folio}? Volverá a aparecer en los historiales.`)) return;
    setRestaurando(item._id);
    try {
      await api.post(`${endpoint}/${item._id}/restaurar`, {});
      setItems(prev => prev.filter(i => !(i._id === item._id && i._tipo === item._tipo)));
      toast_(`${item._tipo} ${item.folio} restaurada`);
    } catch (e) { toast_(e.message, 'error'); }
    finally { setRestaurando(null); }
  };

  const setF = (k, v) => setFiltros(f => ({ ...f, [k]: v }));

  return (
    <div style={{ fontFamily: 'Inter, sans-serif' }}>
      <Toast msg={toast?.msg} tipo={toast?.tipo} onClose={() => setToast(null)} />

      <PageHeader
        icon="🗑️"
        title="Papelera de Solicitudes Borradas"
        subtitle={`${items.length} registro${items.length !== 1 ? 's' : ''} borrado${items.length !== 1 ? 's' : ''}`}
        actions={
          <button onClick={load} style={{ ...css.btnGhost, border: '1px solid var(--hair)', fontSize: 12 }}>
            🔄 Actualizar
          </button>
        }
      />

      {/* Filtros */}
      <div style={{ ...css.card, padding: 0, marginBottom: 16, overflow: 'hidden' }}>
        <div style={{ padding: '9px 18px', background: 'var(--hair-soft)', borderBottom: '1px solid var(--hair)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>🔍 Filtros</span>
        </div>
        <div style={{ padding: '12px 18px', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={filtros.tipo} onChange={e => setF('tipo', e.target.value)} style={{ ...css.select(false), width: 180 }}>
            <option value="">Todos los tipos</option>
            {FUENTES.map(f => <option key={f.tipo} value={f.tipo}>{f.tipo}</option>)}
          </select>
          <select value={filtros.empresa} onChange={e => setF('empresa', e.target.value)} style={{ ...css.select(false), width: 190 }}>
            <option value="">🏢 Todas las compañías</option>
            {empresas.map(e => <option key={e._id || e.nombre} value={e.nombre}>{e.nombre}</option>)}
          </select>
          <input value={solicInput} onChange={e => setSolicInput(e.target.value)}
            placeholder="👤 Buscar solicitante..." style={{ ...css.input(false), width: 190, alignSelf: 'flex-end' }} />
          <div>
            <span style={{ fontSize: 10, color: 'var(--glass)', display: 'block', marginBottom: 2 }}>Desde</span>
            <input type="date" value={filtros.desde} onChange={e => setF('desde', e.target.value)} style={{ ...css.input(false), width: 148 }} />
          </div>
          <div>
            <span style={{ fontSize: 10, color: 'var(--glass)', display: 'block', marginBottom: 2 }}>Hasta</span>
            <input type="date" value={filtros.hasta} onChange={e => setF('hasta', e.target.value)} style={{ ...css.input(false), width: 148 }} />
          </div>
          <button onClick={() => { setSolicInput(''); setFiltros({ tipo: '', empresa: '', solicitante: '', desde: '', hasta: '' }); }} style={{ ...css.btn('#6b7280', true), fontSize: 12, alignSelf: 'flex-end' }}>Limpiar</button>
        </div>
      </div>

      {/* Tabla */}
      <div style={{ ...css.card, padding: 0, overflow: 'hidden' }}>
        {loading ? <Spinner /> : items.length === 0 ? (
          <div style={{ padding: '56px 32px', textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🗑️</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink-soft)', marginBottom: 5 }}>Sin registros borrados</div>
            <div style={{ fontSize: 13, color: 'var(--glass)' }}>No hay solicitudes borradas para los filtros seleccionados.</div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--panel)', borderBottom: '1px solid var(--hair)' }}>
                  {['Folio', 'Tipo', 'Compañía', 'Beneficiario / Concepto', 'Monto', 'Borrada por', 'Fecha de borrado', 'Motivo', ...(esAdmin ? ['Acciones'] : [])].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10.5, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map(s => (
                  <tr key={`${s._tipo}-${s._id}`} style={{ borderBottom: '1px solid var(--hair-soft)', transition: 'background 0.12s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,84,28,0.04)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      <span style={{ fontFamily: 'Geist Mono, monospace', fontSize: 11.5, color: 'var(--glass)', fontWeight: 700 }}>{s.folio}</span>
                    </td>
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}><Badge color={s._color}>{s._tipo}</Badge></td>
                    <td style={{ padding: '10px 14px', fontWeight: 600, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--ink)' }}>{s.empresa}</td>
                    <td style={{ padding: '10px 14px', color: 'var(--ink-soft)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.proveedor || s.comprobador || '—'}</td>
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', fontWeight: 700, color: 'var(--ink)', fontFamily: 'Geist Mono, monospace', fontSize: 12 }}>
                      {fmtMonto(s.monto)} <span style={{ fontSize: 10.5, color: 'var(--glass)', fontWeight: 500 }}>{s.moneda}</span>
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--ink-soft)', whiteSpace: 'nowrap' }}>{s.borradaPor || '—'}</td>
                    <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--glass)', whiteSpace: 'nowrap' }}>{fmtFecha(s.fechaBorrado)}</td>
                    <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--danger)', maxWidth: 280 }} title={s.motivoBorrado}>{s.motivoBorrado || '—'}</td>
                    {esAdmin && (
                      <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                        <button
                          onClick={() => restaurar(s)}
                          disabled={restaurando === s._id}
                          style={{ ...css.btn('#15803d', false), fontSize: 12, padding: '5px 12px', opacity: restaurando === s._id ? 0.6 : 1 }}>
                          {restaurando === s._id ? '…' : '↩️ Restaurar'}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
